/** Prepare CMS hero images: optional background removal + size cap (default 200 KB). */

const DEFAULT_MAX_BYTES = 200 * 1024;
const DEFAULT_MAX_DIMENSION = 1200;

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read the selected image."));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

function isBackgroundPixel(r, g, b, a, samples) {
  if (a < 12) return true;
  // Near-white / light studio grey
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max >= 218 && max - min <= 30) return true;
  // Match sampled corner colours within tolerance
  for (const sample of samples) {
    const dr = Math.abs(r - sample[0]);
    const dg = Math.abs(g - sample[1]);
    const db = Math.abs(b - sample[2]);
    if (dr + dg + db <= 54 && max >= 180) return true;
  }
  return false;
}

/** Flood-fill near-edge light / studio backgrounds to transparent. */
function removeStudioBackground(canvas) {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const { width: w, height: h } = canvas;
  const imageData = ctx.getImageData(0, 0, w, h);
  const data = imageData.data;
  const visited = new Uint8Array(w * h);
  const queue = [];

  const samples = [
    [0, 0],
    [w - 1, 0],
    [0, h - 1],
    [w - 1, h - 1],
    [Math.floor(w / 2), 0],
    [Math.floor(w / 2), h - 1],
  ].map(([x, y]) => {
    const i = (y * w + x) * 4;
    return [data[i], data[i + 1], data[i + 2]];
  });

  const enqueue = (x, y) => {
    if (x < 0 || y < 0 || x >= w || y >= h) return;
    const idx = y * w + x;
    if (visited[idx]) return;
    const i = idx * 4;
    if (!isBackgroundPixel(data[i], data[i + 1], data[i + 2], data[i + 3], samples)) return;
    visited[idx] = 1;
    queue.push(idx);
  };

  for (let x = 0; x < w; x += 1) {
    enqueue(x, 0);
    enqueue(x, h - 1);
  }
  for (let y = 0; y < h; y += 1) {
    enqueue(0, y);
    enqueue(w - 1, y);
  }

  while (queue.length) {
    const idx = queue.pop();
    const i = idx * 4;
    data[i + 3] = 0;
    const x = idx % w;
    const y = (idx - x) / w;
    enqueue(x + 1, y);
    enqueue(x - 1, y);
    enqueue(x, y + 1);
    enqueue(x, y - 1);
  }

  ctx.putImageData(imageData, 0, 0);
}

function drawScaledImage(img, maxDimension) {
  const scale = Math.min(1, maxDimension / Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height));
  const width = Math.max(1, Math.round((img.naturalWidth || img.width) * scale));
  const height = Math.max(1, Math.round((img.naturalHeight || img.height) * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  return canvas;
}

function scaleCanvas(source, factor) {
  const width = Math.max(1, Math.round(source.width * factor));
  const height = Math.max(1, Math.round(source.height * factor));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(source, 0, 0, width, height);
  return canvas;
}

async function encodeUnderLimit(canvas, maxBytes) {
  // Prefer WebP (with alpha), then PNG.
  const attempts = [
    { type: "image/webp", qualities: [0.82, 0.7, 0.58, 0.45, 0.32] },
    { type: "image/png", qualities: [undefined] },
  ];

  let working = canvas;
  for (let resizePass = 0; resizePass < 6; resizePass += 1) {
    for (const attempt of attempts) {
      for (const quality of attempt.qualities) {
        const blob = await canvasToBlob(working, attempt.type, quality);
        if (blob && blob.size <= maxBytes) {
          return { blob, type: attempt.type };
        }
      }
    }
    working = scaleCanvas(working, 0.82);
  }

  // Last resort: smallest WebP we can produce
  const fallback = await canvasToBlob(working, "image/webp", 0.25);
  if (!fallback) {
    throw new Error("Could not compress the image under 200 KB.");
  }
  if (fallback.size > maxBytes) {
    throw new Error(
      `Image is still ${Math.ceil(fallback.size / 1024)} KB after compression. Please use a simpler image under 200 KB.`,
    );
  }
  return { blob: fallback, type: "image/webp" };
}

/**
 * Process a hero upload: remove studio/light background and shrink to <= maxBytes.
 * @returns {Promise<File>}
 */
export async function prepareCmsHeroImage(
  file,
  {
    maxBytes = DEFAULT_MAX_BYTES,
    maxDimension = DEFAULT_MAX_DIMENSION,
    removeBackground = true,
    fileNamePrefix = "cms-hero",
  } = {},
) {
  if (!file) throw new Error("No image selected.");

  const img = await loadImageFromFile(file);
  let canvas = drawScaledImage(img, maxDimension);
  if (removeBackground) {
    removeStudioBackground(canvas);
  }

  const { blob, type } = await encodeUnderLimit(canvas, maxBytes);
  const ext = type === "image/webp" ? "webp" : "png";
  const base = String(file.name || fileNamePrefix).replace(/\.[^.]+$/, "") || fileNamePrefix;
  return new File([blob], `${base}.${ext}`, { type, lastModified: Date.now() });
}

export const CMS_HERO_MAX_BYTES = DEFAULT_MAX_BYTES;
